export interface PaginationInfo {
  page_no: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

export type QueryParamValue = string | number | null | undefined;

export function canShowPagination(totalRecords: number): boolean {
  return totalRecords > 0;
}

export function buildPaginationLabel(
  pagination: PaginationInfo,
  noRecordsLabel: string,
  showingResultsLabel: string,
  resultsLabel: string,
): string {
  if (!pagination.total_records) {
    return noRecordsLabel;
  }

  const start = (pagination.page_no - 1) * pagination.page_size + 1;
  const end = Math.min(pagination.page_no * pagination.page_size, pagination.total_records);
  return `${showingResultsLabel} ${start} to ${end} of ${pagination.total_records} ${resultsLabel}`;
}

export function buildPaginationItems(totalPages: number, currentPage: number): number[] {
  if (!totalPages) {
    return [];
  }

  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: number[] = [];
  const addPage = (pageNo: number): void => {
    if (pageNo >= 1 && pageNo <= totalPages && !pages.includes(pageNo)) {
      pages.push(pageNo);
    }
  };

  addPage(1);
  const windowStart = Math.max(2, currentPage - 1);
  const windowEnd = Math.min(totalPages - 1, currentPage + 1);
  if (windowStart > 2) {
    pages.push(-1);
  }
  for (let page = windowStart; page <= windowEnd; page += 1) {
    addPage(page);
  }
  if (windowEnd < totalPages - 1) {
    pages.push(-1);
  }
  addPage(totalPages);
  return pages;
}

export function buildQueryString(params: Record<string, QueryParamValue>, ignoredValues: readonly QueryParamValue[] = []): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '' || ignoredValues.includes(value)) {
      return;
    }

    queryParams.set(key, String(value));
  });

  const query = queryParams.toString();
  return query ? `?${query}` : '';
}

export function buildTimestampFileName(prefix: string, date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${prefix}_${year}${month}${day}_${hours}${minutes}${seconds}.xlsx`;
}

export function downloadBlob(blob: Blob, fileName: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export function extractPagination<T extends PaginationInfo>(value: unknown): T | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const pagination = (value as { pagination?: unknown }).pagination;
  if (!pagination || typeof pagination !== 'object') {
    return null;
  }

  const record = pagination as Record<string, unknown>;
  return {
    page_no: Number(record['page_no'] ?? 1),
    page_size: Number(record['page_size'] ?? 10),
    total_records: Number(record['total_records'] ?? 0),
    total_pages: Number(record['total_pages'] ?? 0),
  } as T;
}

export function getApiErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const errorObj = error as { error?: unknown; message?: string };
  const payload = errorObj.error as { msg?: unknown; message?: unknown; detail?: unknown } | undefined;
  const message = payload?.msg ?? payload?.message ?? errorObj.message ?? payload?.detail;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}
